import React, {useRef, useState} from 'react';

import {
  Anchor,
  Button,
  Icon,
  IconButton,
  Popover,
  Text,
  Toggle,
  WithToggle
} from '@albert-io/atomic';
import type {ClassroomModelV1, SubjectModelV2} from '@albert-io/models';
import SubjectSplitCard from 'client/components/SubjectSplitCard/SubjectSplitCard.react';
import withTracking from 'lib/hocs/withTracking';
import classNames from 'classnames';

const TrackedCloseSubjectButton = withTracking(Button, {
  onClick: {
    event: 'click',
    type: 'button',
    feature: 'Class Subjects in Classroom',
    component: 'Subject Card',
    content: 'Close access'
  }
});

const TrackedOpenSubjectButton = withTracking(Button, {
  onClick: {
    event: 'click',
    type: 'button',
    feature: 'Class Subjects in Classroom',
    component: 'Subject Card',
    content: 'Open access'
  }
});

interface Props {
  classroom: ClassroomModelV1;
  handleCloseAccess: (subject: SubjectModelV2) => void;
  handleOpenAccess: (subject: SubjectModelV2) => void;
  handlePartialCreditToggled: (classroomId: string, subject: SubjectModelV2) => void;
  subject: SubjectModelV2;
}

export default function SubjectListCardActions({
  classroom,
  handleCloseAccess,
  handleOpenAccess,
  handlePartialCreditToggled,
  subject
}: Props) {
  const targetRef = useRef(null);

  const isSubjectAdaptive = subject.get('adaptive', false);

  const [localIsPartialCreditEnabled, setLocalIsPartialCreditEnabled] = useState(
    subject
      .get('relationshipMeta')
      .get('classroom')
      .get(classroom.getId())
      .get('partial_credit_enabled') ?? false
  );

  const licensedSubjects = classroom.getMeta().getSchoolLicensedSubjects();
  const groupLicensedSubjects = licensedSubjects.get('subject_group_subject_ids');
  const standaloneLicensedSubjects = licensedSubjects.get('standalone_school_license_subject_ids');
  const isSubjectLicensed =
    groupLicensedSubjects.includes(subject.getId()) ||
    standaloneLicensedSubjects.includes(subject.getId());
  const isAssessmentsOnly =
    !subject.getGuides().isEmpty() &&
    subject.getGuides().every((guide) => {
      return guide.getGuideType() === 'assessment';
    });
  const isRestricted = subject
    .getClassroomRelationships(classroom.getId())
    .getRestrictFreePractice();

  // Adaptive subjects are free — their access toggle is never license-gated (spec R2).
  const isIndependentPracticeAvailable =
    !subject.isHidden() && !isAssessmentsOnly && (isSubjectAdaptive || isSubjectLicensed);

  const toggleId = `${subject.getName()}-partial-credit-enabled`;

  const TrackedButton = isRestricted ? TrackedOpenSubjectButton : TrackedCloseSubjectButton;

  return (
    <div ref={targetRef}>
      <SubjectSplitCard.Bottom
        className='subject-split-card__action-bar px-x2! py-x1! rounded-b-x3! flex justify-between'
        paddingLevel={0}
        roundness='none'
      >
        <div className='gap-x1 flex items-center'>
          {isIndependentPracticeAvailable ? (
            <>
              <Icon
                color={isRestricted ? 'negative' : 'positive'}
                icon={isRestricted ? 'ban' : 'check'}
              />
              <Text bold color={isRestricted ? 'negative' : 'positive'} size='s'>
                {isRestricted ? 'Closed' : 'Opened'} Access
              </Text>
            </>
          ) : (
            <>
              {!isSubjectLicensed ? (
                <Anchor className='no-underline' to='/account/access'>
                  <Button className='gap-x1-half flex' color='secondary' size='s' variant='text'>
                    Needs License
                    <Icon icon='up-right-from-square' />
                  </Button>
                </Anchor>
              ) : (
                <Text bold color='tertiary' size='s'>
                  {subject.isHidden() ? 'Hidden Subject' : 'Practice settings unavailable'}
                </Text>
              )}
            </>
          )}
        </div>
        {isIndependentPracticeAvailable && (
          <WithToggle>
            {({on, onClick}) => (
              <>
                <IconButton icon={['far', 'gear']} label='Settings' onClick={onClick} size='s' />
                {on && (
                  <Popover
                    className='background-primary'
                    paddingLevel={1}
                    // 'bottom-end' is a valid Popper placement passed through to AnchoredFixedPosition
                    // (matched by the [data-placement^='bottom'] SCSS), but is missing from Popover's
                    // over-narrow `positions` allowlist.
                    // @ts-expect-error -- Popover's `position` union omits 'bottom-end'
                    position='bottom-end'
                    targetRef={targetRef}
                    usePortal={false}
                  >
                    <TrackedButton
                      className='hover:background-subtle-hover! w-full justify-start!'
                      onClick={() =>
                        isRestricted ? handleOpenAccess(subject) : handleCloseAccess(subject)
                      }
                      variant='text'
                    >
                      <Text className='font-normal' color={isRestricted ? 'positive' : 'negative'}>
                        {isRestricted ? 'Open' : 'Close'} Access
                      </Text>
                    </TrackedButton>
                    {!isSubjectAdaptive && (
                      <div className='px-x2 py-x1'>
                        <div className='gap-x3 flex justify-between'>
                          <Text
                            as='label'
                            className={classNames({
                              'cursor-not-allowed': isRestricted,
                              'cursor-pointer': !isRestricted
                            })}
                            color={isRestricted ? 'secondary' : undefined}
                            htmlFor={toggleId}
                          >
                            Enable Partial Credit
                          </Text>
                          <Toggle
                            checked={localIsPartialCreditEnabled}
                            disabled={isRestricted}
                            id={toggleId}
                            onChange={() => {
                              setLocalIsPartialCreditEnabled(
                                (isPartialCreditEnabled) => !isPartialCreditEnabled
                              );
                              handlePartialCreditToggled(classroom.getId(), subject);
                            }}
                          />
                        </div>
                        {isRestricted && (
                          <Text color='secondary' size='xs'>
                            Open access to subject to adjust settings
                          </Text>
                        )}
                      </div>
                    )}
                  </Popover>
                )}
              </>
            )}
          </WithToggle>
        )}
      </SubjectSplitCard.Bottom>
    </div>
  );
}
